import {
  Lock,
  RefreshCw,
  Server,
  ShieldCheck,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DataTable, type Column } from "../../../components/DataTable";
import { RowsPerCardSelect } from "../../../components/RowsPerCardSelect";
import { SearchInput } from "../../../components/SearchInput";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Tip } from "../../../components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { formatDateTime, MAX_PAGE_SIZE } from "../../../../utils/formatters";
import { useToastStore } from "../../../../stores/toastStore";
import { pagErrorMessage } from "../helpers/pagHelpers";
import {
  fetchPagCyberarkAccounts,
  fetchPagCyberarkPlatforms,
  type PagCyberarkAccountItem,
  type PagCyberarkPlatformItem,
} from "../services/pagApi";
import { SimpleBreakdownPagination } from "./SimpleBreakdownPagination";

/* ── CyberArk Accounts Drilldown Sheet Component ─────────────────────── */

interface CyberarkAccountsDrilldownSheetProps {
  platform: PagCyberarkPlatformItem | null;
  accounts: PagCyberarkAccountItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  search: string;
  sortKey: string;
  sortDir: "asc" | "desc";
  onClose: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
  onSort: (key: string) => void;
  onRefresh: () => void;
}

export function CyberarkAccountsDrilldownSheet({
  platform,
  accounts,
  loading,
  page,
  pageSize,
  totalElements,
  totalPages,
  search,
  sortKey,
  sortDir,
  onClose,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onSearchSubmit,
  onSort,
  onRefresh,
}: CyberarkAccountsDrilldownSheetProps) {

  const accountColumns = useMemo<Column<PagCyberarkAccountItem>[]>(
    () => [
      {
        key: "name",
        header: "Name",
        width: "18%",
        sortable: true,
        maxChars: false,
        render: (row) => {
          const isLong = (row.name?.length ?? 0) > 80;
          return (
            <div className="flex items-start gap-2 min-w-0" title={row.name}>
              <Lock className="size-3.5 text-indigo-400 shrink-0 mt-0.5" />
              <span
                className={`font-medium text-foreground ${
                  isLong ? "whitespace-normal break-all" : "whitespace-nowrap"
                }`}
              >
                {row.name}
              </span>
            </div>
          );
        },
      },
      {
        key: "userName",
        header: "Username",
        width: "16%",
        sortable: true,
        render: (row) => (
          <span className="truncate font-medium text-foreground" title={row.userName}>
            {row.userName}
          </span>
        ),
      },
      {
        key: "address",
        header: "Address",
        width: "18%",
        sortable: true,
        render: (row) => (
          <span className="truncate text-muted-foreground" title={row.address ?? undefined}>
            {row.address || "-"}
          </span>
        ),
      },
      {
        key: "safeName",
        header: "Safe",
        width: "12%",
        sortable: true,
        render: (row) => (
          <span
            className="rounded bg-muted/60 border border-border/50 px-1.5 py-0.5 text-xs font-mono text-muted-foreground truncate"
            title={row.safeName}
          >
            {row.safeName}
          </span>
        ),
      },
      {
        key: "secretType",
        header: "Secret Type",
        width: "12%",
        sortable: true,
        render: (row) => (
          <span className="capitalize text-xs text-muted-foreground">
            {row.secretType || "-"}
          </span>
        ),
      },
      {
        key: "automaticManagementEnabled",
        header: "Auto Managed",
        width: "12%",
        sortable: true,
        render: (row) => {
          if (row.automaticManagementEnabled) {
            return (
              <span className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-400">
                <CheckCircle2 className="size-3" />
                Enabled
              </span>
            );
          }

          const accountRecord = row as Record<string, unknown>;
          const reason =
            row.manualManagementReason ||
            (accountRecord["manual_management_reason"] as string | undefined) ||
            "-";

          return (
            <Tip
              label={
                <div className="py-0.5 text-xs text-left max-w-xs">
                  <span className="font-medium text-muted-foreground">Reason: </span>
                  <span className="font-semibold text-foreground">{String(reason)}</span>
                </div>
              }
              side="top"
              align="center"
            >
              <span className="inline-flex items-center gap-1 rounded border border-zinc-500/30 bg-zinc-500/10 px-2 py-0.5 text-xs font-medium text-muted-foreground cursor-default">
                <XCircle className="size-3" />
                Disabled
              </span>
            </Tip>
          );
        },
      },
      {
        key: "managementStatus",
        header: "Status",
        width: "14%",
        sortable: true,
        render: (row) => {
          if (!row.automaticManagementEnabled) {
            return (
              <span className="inline-flex items-center rounded border border-zinc-500/30 bg-zinc-500/10 px-2 py-0.5 text-xs font-medium text-muted-foreground whitespace-nowrap">
                Not Applicable
              </span>
            );
          }

          if (!row.managementStatus) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }

          return (
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium border capitalize whitespace-nowrap ${
                row.managementStatus.toLowerCase() === "success"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-400"
              }`}
            >
              {row.managementStatus}
            </span>
          );
        },
      },
      {
        key: "lastModified",
        header: "Last Modified",
        width: "14%",
        sortable: true,
        render: (row) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap" title={row.lastModified ? formatDateTime(row.lastModified) : undefined}>
            {row.lastModified ? formatDateTime(row.lastModified) : "-"}
          </span>
        ),
      },
      {
        key: "lastVerifiedTime",
        header: "Last Verified",
        width: "14%",
        sortable: true,
        render: (row) => {
          const val =
            row.lastVerifiedTime ??
            row.lastVerified ??
            (row as Record<string, unknown>)["last_verified_time"] ??
            (row as Record<string, unknown>)["last_verified"];
          return (
            <span className="text-xs text-muted-foreground whitespace-nowrap" title={val ? formatDateTime(val as string | number) : undefined}>
              {val ? formatDateTime(val as string | number) : "-"}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <Sheet
      open={platform !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex h-full max-h-screen w-[90vw] max-w-none flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="gap-1.5 border-b border-border p-5 pr-14 text-left shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-indigo-500/10">
              <ShieldCheck className="size-4 text-indigo-400" />
            </div>
            <SheetTitle className="text-base font-semibold">
              CyberArk Platform Accounts
            </SheetTitle>
          </div>
          <SheetDescription className="text-xs text-muted-foreground">
            {platform
              ? `Safe: ${platform.safeName} · Platform: ${platform.platformId || "Default"} · ${totalElements} account${totalElements === 1 ? "" : "s"}`
              : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 flex flex-col p-5 overflow-hidden">
          {/* Top toolbar */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="border-cyan-500/20 bg-cyan-500/10 text-cyan-400">
                Source: {platform?.sourceSystemName || "CyberArk"}
              </Badge>
              <Badge variant="secondary" className="border-indigo-500/20 bg-indigo-500/10 text-indigo-400">
                Safe: {platform?.safeName}
              </Badge>
              {platform?.platformId && (
                <Badge variant="secondary" className="border-cyan-500/20 bg-cyan-500/10 text-cyan-400">
                  Platform: {platform.platformId}
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                size="sm"
                className="w-52"
                value={search}
                onChange={onSearchChange}
                onSubmit={onSearchSubmit}
                placeholder="Search accounts..."
                ariaLabel="Search accounts"
              />
              <RowsPerCardSelect
                value={pageSize}
                onChange={onPageSizeChange}
                label="Rows per page"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="h-[38px] px-3"
                title="Refresh accounts"
              >
                <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {/* Drilldown Table */}
          <div className="min-h-0 flex-1 flex flex-col">
            <DataTable
              columns={accountColumns}
              data={accounts}
              keyField="id"
              loading={loading}
              minTableWidth="980px"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              emptyMessage={
                search
                  ? `No accounts match "${search}"`
                  : "No Data Available"
              }
            />
          </div>

          <SimpleBreakdownPagination
            currentPage={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            loading={loading}
            onPageChange={onPageChange}
            itemLabel="accounts"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ── CyberArk Platforms Breakdown Main Panel ─────────────────────────── */

export interface CyberarkPlatformsBreakdownProps {
  sourceSystemIds?: number[];
  reloadToken?: number;
}

export function CyberarkPlatformsBreakdown({
  sourceSystemIds = [],
  reloadToken = 0,
}: CyberarkPlatformsBreakdownProps) {
  const [platforms, setPlatforms] = useState<PagCyberarkPlatformItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(MAX_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (!value.trim()) {
      setAppliedSearch("");
      setPage(0);
    }
  };

  const handleSearchSubmit = (term: string) => {
    const next = term.trim();
    if (next === appliedSearch) return;
    setAppliedSearch(next);
    setPage(0);
  };
  const [sortKey, setSortKey] = useState<string>("usernameCount");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedPlatform, setSelectedPlatform] = useState<PagCyberarkPlatformItem | null>(null);

  // Drilldown state (lifted from CyberarkAccountsDrilldownSheet — no useEffect needed)
  const [drilldownAccounts, setDrilldownAccounts] = useState<PagCyberarkAccountItem[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownPage, setDrilldownPage] = useState(0);
  const [drilldownPageSize, setDrilldownPageSize] = useState(MAX_PAGE_SIZE);
  const [drilldownTotalElements, setDrilldownTotalElements] = useState(0);
  const [drilldownTotalPages, setDrilldownTotalPages] = useState(1);
  const [drilldownSearch, setDrilldownSearch] = useState("");
  const [drilldownSortKey, setDrilldownSortKey] = useState<string>("name");
  const [drilldownSortDir, setDrilldownSortDir] = useState<"asc" | "desc">("asc");

  // Synchronize CyberArk platforms breakdown data from backend API
  useEffect(() => {
    if (sourceSystemIds.length === 0) {
      setPlatforms([]);
      setTotalElements(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    let isCancelled = false;
    setLoading(true);

    fetchPagCyberarkPlatforms(
      sourceSystemIds,
      page,
      pageSize,
      appliedSearch,
      sortKey,
      sortDir,
    )
      .then((res) => {
        if (!isCancelled && res) {
          setPlatforms(res.content ?? []);
          setTotalElements(res.totalElements ?? (res.content?.length ?? 0));
          setTotalPages(res.totalPages ?? 1);
        }
      })
      .catch((err: unknown) => {
        if (!isCancelled) {
          useToastStore
            .getState()
            .showError(
              "Could not load CyberArk platforms",
              pagErrorMessage(err, "Please retry in a moment."),
            );
        }
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [sourceSystemIds, page, pageSize, appliedSearch, sortKey, sortDir, reloadToken]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
    setPage(0);
  };

  // Drilldown data loader — called directly from event handlers, not useEffect
  const loadDrilldownAccounts = (
    plat: PagCyberarkPlatformItem,
    pg: number,
    pgSize: number,
    srch: string,
    sKey: string,
    sDir: "asc" | "desc",
  ) => {
    setDrilldownLoading(true);
    fetchPagCyberarkAccounts(
      {
        sourceSystemId: plat.sourceSystemId,
        platformId: plat.platformId,
        safeName: plat.safeName,
        search: srch.trim() || undefined,
        sortBy: sKey,
        sortDir: sDir,
      },
      pg,
      pgSize,
    )
      .then((res) => {
        if (res) {
          setDrilldownAccounts(res.content ?? []);
          setDrilldownTotalElements(res.totalElements ?? (res.content?.length ?? 0));
          setDrilldownTotalPages(res.totalPages ?? 1);
        }
      })
      .catch((err: unknown) => {
        useToastStore
          .getState()
          .showError(
            "Could not load platform accounts",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      })
      .finally(() => {
        setDrilldownLoading(false);
      });
  };

  const handleOpenDrilldown = (plat: PagCyberarkPlatformItem) => {
    setSelectedPlatform(plat);
    setDrilldownPage(0);
    setDrilldownPageSize(100);
    setDrilldownSearch("");
    setDrilldownSortKey("name");
    setDrilldownSortDir("asc");
    setDrilldownAccounts([]);
    setDrilldownTotalElements(0);
    setDrilldownTotalPages(1);
    loadDrilldownAccounts(plat, 0, 100, "", "name", "asc");
  };

  const handleDrilldownPageChange = (newPage: number) => {
    setDrilldownPage(newPage);
    if (selectedPlatform) {
      loadDrilldownAccounts(selectedPlatform, newPage, drilldownPageSize, drilldownSearch, drilldownSortKey, drilldownSortDir);
    }
  };

  const handleDrilldownPageSizeChange = (newSize: number) => {
    setDrilldownPageSize(newSize);
    setDrilldownPage(0);
    if (selectedPlatform) {
      loadDrilldownAccounts(selectedPlatform, 0, newSize, drilldownSearch, drilldownSortKey, drilldownSortDir);
    }
  };

  const handleDrilldownSearchSubmit = (term: string) => {
    setDrilldownSearch(term);
    setDrilldownPage(0);
    if (selectedPlatform) {
      loadDrilldownAccounts(selectedPlatform, 0, drilldownPageSize, term, drilldownSortKey, drilldownSortDir);
    }
  };

  const handleDrilldownSort = (key: string) => {
    let newDir: "asc" | "desc" = "asc";
    if (drilldownSortKey === key) {
      newDir = drilldownSortDir === "asc" ? "desc" : "asc";
    }
    setDrilldownSortKey(key);
    setDrilldownSortDir(newDir);
    setDrilldownPage(0);
    if (selectedPlatform) {
      loadDrilldownAccounts(selectedPlatform, 0, drilldownPageSize, drilldownSearch, key, newDir);
    }
  };

  const handleDrilldownRefresh = () => {
    if (selectedPlatform) {
      loadDrilldownAccounts(selectedPlatform, drilldownPage, drilldownPageSize, drilldownSearch, drilldownSortKey, drilldownSortDir);
    }
  };

  const handleCloseDrilldown = () => {
    setSelectedPlatform(null);
    setDrilldownAccounts([]);
    setDrilldownTotalElements(0);
    setDrilldownTotalPages(1);
  };

  const totalUsernames = useMemo(() => {
    return platforms.reduce((acc, p) => acc + (p.usernameCount || 0), 0);
  }, [platforms]);

  const platformColumns = useMemo<Column<PagCyberarkPlatformItem>[]>(
    () => [
      {
        key: "safeName",
        header: "Safe Name",
        width: "32%",
        sortable: true,
        render: (row) => (
          <div className="flex min-w-0 items-center gap-2">
            <div className="size-1.5 shrink-0 rounded-full bg-cyan-400" />
            <span
              className="truncate font-medium text-foreground"
              title={row.safeName}
            >
              {row.safeName}
            </span>
          </div>
        ),
      },
      {
        key: "sourceSystemName",
        header: "Source System",
        width: "22%",
        render: (row) => (
          <span className="inline-flex items-center rounded border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-xs font-medium text-cyan-400">
            {row.sourceSystemName || "CyberArk"}
          </span>
        ),
      },
      {
        key: "platformId",
        header: "Platform ID",
        width: "28%",
        sortable: true,
        render: (row) => (
          <span
            className="inline-flex max-w-full items-center rounded border border-border/60 bg-muted/40 px-2 py-0.5 truncate font-mono text-xs text-foreground"
            title={row.platformId || "Default (None)"}
          >
            {row.platformId || (
              <span className="text-muted-foreground/60 italic font-normal">
                (None)
              </span>
            )}
          </span>
        ),
      },
      {
        key: "usernameCount",
        header: "Accounts",
        width: "18%",
        align: "center",
        headerAlign: "center",
        sortable: true,
        render: (row) =>
          row.usernameCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleOpenDrilldown(row)}
              className="h-auto p-0 font-semibold tabular-nums text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer transition-colors bg-transparent hover:bg-transparent"
              title={`View ${row.usernameCount} account${row.usernameCount === 1 ? "" : "s"} in ${row.safeName || row.platformId}`}
            >
              {row.usernameCount.toLocaleString()}
            </Button>
          ) : (
            <span className="tabular-nums text-muted-foreground">0</span>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="flex flex-col gap-5 rounded-2xl border border-border/80 bg-card p-5 lg:flex-row shadow-sm">
        {/* Left Stat Card */}
        <div className="flex w-full shrink-0 flex-col rounded-xl border border-cyan-400/80 bg-cyan-50/20 dark:bg-cyan-500/[0.04] p-5 lg:w-[200px]">
          <div className="mb-3 flex size-9 items-center justify-center rounded-lg bg-cyan-500/10">
            <Server className="size-5 text-cyan-400" />
          </div>
          <div
            onClick={() => {
              if (platforms.length > 0) handleOpenDrilldown(platforms[0]);
            }}
            className="text-4xl font-bold text-foreground cursor-pointer hover:text-cyan-400 transition-colors"
            title="Total CyberArk accounts"
          >
            {totalUsernames.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">Total Accounts</div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {totalElements} CyberArk Platforms
          </div>
          <div className="mt-auto pt-3 text-[11px] text-cyan-400">
            Safes &amp; Platforms
          </div>
        </div>

        {/* Right Table Container */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-cyan-500/10">
              <Server className="size-4 text-cyan-400" />
            </div>
            <h3 className="font-semibold text-foreground">CyberArk Platforms Breakdown</h3>
            <Badge variant="secondary" className="border-cyan-500/20 bg-cyan-500/10 text-cyan-400">
              Total Platforms: {totalElements}
            </Badge>
            <Badge variant="secondary" className="border-indigo-500/20 bg-indigo-500/10 text-indigo-400">
              Total Accounts: {totalUsernames}
            </Badge>

            <div className="ml-auto flex flex-wrap items-center gap-3">
              <SearchInput
                size="sm"
                className="w-48"
                value={search}
                onChange={handleSearchChange}
                onSubmit={(term) => {
                  handleSearchSubmit(term);
                  setPage(0);
                }}
                disabled={loading}
                placeholder="Search"
                ariaLabel="Search CyberArk platforms"
              />
              <RowsPerCardSelect
                value={pageSize}
                onChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                label="Rows per page"
              />
            </div>
          </div>

          <div className="mt-1 flex min-h-0 flex-1 flex-col">
            <DataTable
              borderless
              columns={platformColumns}
              data={platforms}
              rowKey={(row, idx) =>
                `${row.sourceSystemId}-${row.safeName}-${row.platformId || "empty"}-${idx}`
              }
              loading={loading}
              maxHeight="460px"
              minTableWidth="760px"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              emptyMessage={
                search
                  ? `No platforms match "${search}"`
                  : "No Data Available"
              }
            />
          </div>

          <SimpleBreakdownPagination
            currentPage={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            itemLabel="platforms"
          />
        </div>
      </div>

      {/* Drilldown Drawer / Sheet */}
      <CyberarkAccountsDrilldownSheet
        platform={selectedPlatform}
        accounts={drilldownAccounts}
        loading={drilldownLoading}
        page={drilldownPage}
        pageSize={drilldownPageSize}
        totalElements={drilldownTotalElements}
        totalPages={drilldownTotalPages}
        search={drilldownSearch}
        sortKey={drilldownSortKey}
        sortDir={drilldownSortDir}
        onClose={handleCloseDrilldown}
        onPageChange={handleDrilldownPageChange}
        onPageSizeChange={handleDrilldownPageSizeChange}
        onSearchChange={setDrilldownSearch}
        onSearchSubmit={handleDrilldownSearchSubmit}
        onSort={handleDrilldownSort}
        onRefresh={handleDrilldownRefresh}
      />
    </>
  );
}
